import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useHistorialImportaciones } from '@/hooks/useHistorialImportaciones';
import { cn } from '@/lib/utils';
import type { EstadoImportacion, ImportacionResumen } from '@adelante/shared';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { FechaCR } from './FechaCR';

interface Props {
  subiendoArchivo?: string | null;
}

const TEXTO_ESTADO: Record<EstadoImportacion, string> = {
  ok: 'OK',
  parcial: 'Parcial',
  duplicado_archivo: 'Duplicado',
  procesando: 'Procesando',
};

const EXPLICACION_ESTADO: Record<EstadoImportacion, string> = {
  ok: 'Todos los batches del archivo fueron insertados sin errores ni omisiones.',
  parcial:
    'El archivo se procesó, pero algunos batches fueron omitidos por duplicado o tuvieron errores de parseo.',
  duplicado_archivo:
    'Este archivo (mismo hash SHA-256) ya había sido ingestado previamente. No se hicieron cambios.',
  procesando: 'La ingesta está en curso o quedó en este estado por una interrupción.',
};

function BadgeEstado({ estado }: { estado: EstadoImportacion }) {
  const styles: Record<EstadoImportacion, string> = {
    ok: 'bg-adelante-primary/20 text-adelante-primary-dark border-transparent hover:bg-adelante-primary/30',
    parcial: 'bg-yellow-100 text-yellow-900 border-transparent hover:bg-yellow-100/80',
    duplicado_archivo: 'bg-zinc-200 text-zinc-700 border-transparent hover:bg-zinc-200/80',
    procesando: 'bg-blue-100 text-blue-900 border-transparent hover:bg-blue-100/80',
  };
  return (
    <Badge variant="outline" className={styles[estado]}>
      {TEXTO_ESTADO[estado]}
    </Badge>
  );
}

function FilaImportacion({ item }: { item: ImportacionResumen }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <Collapsible asChild open={abierto} onOpenChange={setAbierto}>
      <>
        <CollapsibleTrigger asChild>
          <TableRow className="cursor-pointer hover:bg-muted/50">
            <TableCell className="w-8">
              <ChevronRight
                className={cn('h-4 w-4 transition-transform', abierto && 'rotate-90')}
              />
            </TableCell>
            <TableCell>
              <FechaCR fecha={item.fecha_archivo} formato="corto" />
            </TableCell>
            <TableCell className="font-mono text-xs">{item.archivo_nombre}</TableCell>
            <TableCell>
              <BadgeEstado estado={item.estado} />
            </TableCell>
            <TableCell className="text-right tabular-nums">{item.batches_nuevos}</TableCell>
            <TableCell className="text-right tabular-nums">{item.batches_duplicados}</TableCell>
            <TableCell className="text-right tabular-nums">{item.batches_con_error}</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {item.usuario_email ?? '—'}
            </TableCell>
          </TableRow>
        </CollapsibleTrigger>
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/30">
            <TableCell colSpan={8}>
              <div className="grid grid-cols-1 gap-2 px-6 py-3 text-sm md:grid-cols-2">
                <div>
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Fecha completa
                  </span>
                  <p className="font-mono text-xs">
                    {new Date(item.fecha_archivo).toLocaleString('es-CR')}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Filas totales en archivo
                  </span>
                  <p className="tabular-nums">{item.filas_totales}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Hash SHA-256
                  </span>
                  <p className="break-all font-mono text-xs">{item.archivo_hash}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    Estado
                  </span>
                  <p>{EXPLICACION_ESTADO[item.estado]}</p>
                </div>
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}

export function HistorialImportaciones({ subiendoArchivo }: Props) {
  const { data, isLoading, error } = useHistorialImportaciones({ limite: 10, offset: 0 });
  const items = data?.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de importaciones</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Fecha</TableHead>
              <TableHead>Archivo</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Nuevos</TableHead>
              <TableHead className="text-right">Duplicados</TableHead>
              <TableHead className="text-right">Errores</TableHead>
              <TableHead>Usuario</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subiendoArchivo && (
              <TableRow className="bg-blue-50/50">
                <TableCell>
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">ahora</TableCell>
                <TableCell className="font-mono text-xs">{subiendoArchivo}</TableCell>
                <TableCell colSpan={5} className="text-sm italic text-muted-foreground">
                  Procesando archivo…
                </TableCell>
              </TableRow>
            )}
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Skeleton className="h-8 w-full" />
                </TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={8} className="text-destructive">
                  Error al cargar historial: {error.message}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !error && items.length === 0 && !subiendoArchivo && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Sin importaciones registradas.
                </TableCell>
              </TableRow>
            )}
            {items.map((item) => (
              <FilaImportacion key={item.id} item={item} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
